import { useState, useEffect } from 'react';
import { X, ChevronRight, Building2, Home, Plane, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { getProperties, getUnits, createTenant } from '@/lib/api';
import { sortPropertiesByBuildingId, sortUnitsNumerically } from './tenantUtils';
import { toast } from 'sonner';
import { formatUnitLabel } from '@/lib/unitLabel';
import { StartEndDateFields } from '@/components/common/DatePickers';

function StepChip({ label }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 mr-1.5 mb-1">
      {label}
    </span>
  );
}

export function TenantCreatePanel({ open, onClose, onCreated, prefill }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  const [properties, setProperties] = useState([]);
  const [allUnits, setAllUnits] = useState([]);

  // Form fields
  const [propertyId, setPropertyId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [isAirbnb, setIsAirbnb] = useState(null); // null = not chosen yet
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [moveInDate, setMoveInDate] = useState('');
  const [moveOutDate, setMoveOutDate] = useState('');
  const [monthlyRent, setMonthlyRent] = useState('');
  const [totalRent, setTotalRent] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [rentDueDate, setRentDueDate] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDate, setDepositDate] = useState('');
  const [partialFirstMonth, setPartialFirstMonth] = useState('');
  const [partialLastMonth, setPartialLastMonth] = useState('');
  const [isM2M, setIsM2M] = useState(false);
  const [pets, setPets] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      resetForm();
      loadData();
    }
  }, [open]);

  // Handle prefill (e.g. from PropertyCard's "Add Tenant" or from CurrentFutureTab)
  useEffect(() => {
    if (open && prefill) {
      if (prefill.property_id) { setPropertyId(prefill.property_id); setStep(2); }
      if (prefill.unit_id) { setUnitId(prefill.unit_id); setStep(3); }
    }
  }, [open, prefill]);

  const loadData = async () => {
    try {
      const [p, u] = await Promise.all([getProperties(), getUnits()]);
      setProperties(sortPropertiesByBuildingId(p));
      setAllUnits(u);
    } catch {}
  };

  const resetForm = () => {
    setStep(1);
    setPropertyId(''); setUnitId(''); setIsAirbnb(null);
    setName(''); setPhone(''); setEmail('');
    setMoveInDate(''); setMoveOutDate('');
    setMonthlyRent(''); setTotalRent('');
    setPaymentMethod(''); setRentDueDate('');
    setDepositAmount(''); setDepositDate('');
    setPartialFirstMonth(''); setPartialLastMonth('');
    setIsM2M(false); setPets(''); setNotes('');
    setSaving(false); setShowDiscard(false);
  };

  const handleClose = () => {
    if (step > 1) setShowDiscard(true);
    else onClose();
  };

  const filteredUnits = sortUnitsNumerically(allUnits.filter(u => u.property_id === propertyId));

  // Airbnb nightly calculation
  const nights = (() => {
    if (moveInDate && moveOutDate) {
      const diff = Math.floor((new Date(moveOutDate) - new Date(moveInDate)) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 0;
    }
    return 0;
  })();
  const perNight = totalRent && nights > 0 ? (parseFloat(totalRent) / nights).toFixed(2) : 0;

  const handleCreate = async () => {
    if (!propertyId || !unitId || !name.trim() || !moveInDate || !moveOutDate) {
      toast.error('Please fill all required fields'); return;
    }
    if (!isAirbnb && !monthlyRent) { toast.error('Monthly rent is required'); return; }
    if (isAirbnb && !totalRent) { toast.error('Total rent is required'); return; }

    setSaving(true);
    try {
      await createTenant({
        property_id: propertyId, unit_id: unitId,
        name: name.trim(), phone, email,
        move_in_date: moveInDate, move_out_date: moveOutDate,
        is_airbnb_vrbo: isAirbnb,
        is_m2m: !isAirbnb ? isM2M : false,
        monthly_rent: monthlyRent ? parseFloat(monthlyRent) : null,
        total_rent: totalRent ? parseFloat(totalRent) : null,
        payment_method: paymentMethod, rent_due_date: rentDueDate,
        deposit_amount: depositAmount ? parseFloat(depositAmount) : null,
        deposit_date: depositDate || null,
        partial_first_month: partialFirstMonth ? parseFloat(partialFirstMonth) : null,
        partial_last_month: partialLastMonth ? parseFloat(partialLastMonth) : null,
        pets, notes,
      });
      toast.success('Tenant created');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tenant');
    } finally { setSaving(false); }
  };

  const selectedPropName = properties.find(p => p.id === propertyId)?.name;
  const selectedUnitNum = allUnits.find(u => u.id === unitId)?.unit_number;
  const summaryChips = [];
  if (selectedPropName) summaryChips.push(selectedPropName);
  if (selectedUnitNum) summaryChips.push(`Unit ${selectedUnitNum}`);
  if (isAirbnb !== null && step > 3) summaryChips.push(isAirbnb ? 'Airbnb/VRBO' : 'Long Term');

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={handleClose} />
      <div
        className="fixed top-0 right-0 h-full w-[420px] max-w-full bg-white z-50 shadow-xl flex flex-col"
        style={{ borderLeft: '0.5px solid #e5e7eb', animation: 'slideInRight 200ms ease-out' }}
        data-testid="tenant-create-panel"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-[15px] font-semibold text-gray-900">New Tenant</h2>
          <button onClick={handleClose} className="p-1 rounded-md hover:bg-gray-100" data-testid="tenant-create-close">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {showDiscard && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <span className="text-[13px] text-amber-700">Discard changes?</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowDiscard(false)}>Keep editing</Button>
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => { resetForm(); onClose(); }}>Discard</Button>
            </div>
          </div>
        )}

        {summaryChips.length > 0 && (
          <div className="px-5 pt-3 pb-1 flex flex-wrap">
            {summaryChips.map((c, i) => <StepChip key={i} label={c} />)}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* STEP 1: Select Property */}
          <div>
            <p className="text-[13px] font-semibold text-gray-700 mb-3">1. Property</p>
            <Select value={propertyId} onValueChange={v => { setPropertyId(v); setUnitId(''); if (step < 2) setStep(2); }}>
              <SelectTrigger className="h-9 text-[13px]" data-testid="tenant-create-property-select">
                <SelectValue placeholder="Select a property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}{p.building_id != null ? ` (#${p.building_id})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* STEP 2: Select Unit */}
          {step >= 2 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">2. Unit</p>
              {filteredUnits.length === 0 ? (
                <p className="text-[12px] text-gray-400">No units for this property</p>
              ) : (
                <Select value={unitId} onValueChange={v => { setUnitId(v); if (step < 3) setStep(3); }}>
                  <SelectTrigger className="h-9 text-[13px]" data-testid="tenant-create-unit-select">
                    <SelectValue placeholder="Select a unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredUnits.map(u => (
                      <SelectItem key={u.id} value={u.id}>{formatUnitLabel(u, properties)} ({u.unit_size})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* STEP 3: Airbnb vs Long Term */}
          {step >= 3 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">3. Rental type</p>
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  onClick={() => { setIsAirbnb(true); if (step < 4) setStep(4); }}
                  className={`flex flex-col items-center justify-center py-4 px-3 rounded-lg border-2 transition-all ${
                    isAirbnb === true ? 'border-sky-500 bg-sky-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="tenant-type-airbnb"
                >
                  <Plane className={`h-6 w-6 mb-1.5 ${isAirbnb === true ? 'text-sky-600' : 'text-gray-400'}`} />
                  <span className={`text-[13px] font-medium ${isAirbnb === true ? 'text-sky-700' : 'text-gray-600'}`}>Airbnb / VRBO</span>
                </button>
                <button
                  onClick={() => { setIsAirbnb(false); if (step < 4) setStep(4); }}
                  className={`flex flex-col items-center justify-center py-4 px-3 rounded-lg border-2 transition-all ${
                    isAirbnb === false ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="tenant-type-longterm"
                >
                  <Briefcase className={`h-6 w-6 mb-1.5 ${isAirbnb === false ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <span className={`text-[13px] font-medium ${isAirbnb === false ? 'text-emerald-700' : 'text-gray-600'}`}>Long Term</span>
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Details */}
          {step >= 4 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">4. Tenant details</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px] text-gray-500">Name *</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Tenant name" className="h-9 text-[13px] mt-1" data-testid="tenant-create-name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[12px] text-gray-500">Phone</Label>
                    <Input value={phone} onChange={e => setPhone(e.target.value)} className="h-9 text-[13px] mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px] text-gray-500">Email</Label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="h-9 text-[13px] mt-1" />
                  </div>
                </div>
                <StartEndDateFields
                  start={moveInDate}
                  end={moveOutDate}
                  onChange={({ start, end }) => { setMoveInDate(start); setMoveOutDate(end); }}
                  startLabel="Move-in Date *"
                  endLabel="Move-out Date *"
                  startTestId="tenant-create-movein"
                  endTestId="tenant-create-moveout"
                />

                {/* Airbnb-specific fields */}
                {isAirbnb && (
                  <>
                    <div>
                      <Label className="text-[12px] text-gray-500">Total Rent *</Label>
                      <Input type="number" value={totalRent} onChange={e => setTotalRent(e.target.value)} className="h-9 text-[13px] mt-1" data-testid="tenant-create-total-rent" />
                    </div>
                    {nights > 0 && totalRent && (
                      <Card className="bg-sky-50 border-sky-200">
                        <CardContent className="pt-3 pb-3">
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                              <p className="text-[10px] text-muted-foreground uppercase">Nights</p>
                              <p className="font-semibold text-sm tabular-nums">{nights}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground uppercase">Per Night</p>
                              <p className="font-semibold text-sm tabular-nums">${perNight}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground uppercase">Total</p>
                              <p className="font-semibold text-sm tabular-nums">${parseFloat(totalRent).toLocaleString()}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}

                {/* Long-term-specific fields */}
                {!isAirbnb && (
                  <>
                    <div className="flex items-center gap-3 p-2.5 rounded-lg border bg-amber-50/60 border-amber-200/60">
                      <Switch checked={isM2M} onCheckedChange={setIsM2M} />
                      <Label className="text-[12px] cursor-pointer">Month-to-Month (M2M)</Label>
                    </div>
                    <div>
                      <Label className="text-[12px] text-gray-500">Monthly Rent *</Label>
                      <Input type="number" value={monthlyRent} onChange={e => setMonthlyRent(e.target.value)} className="h-9 text-[13px] mt-1" data-testid="tenant-create-monthly-rent" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[12px] text-gray-500">Payment Method</Label>
                        <Input value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} placeholder="e.g. Zelle, Check" className="h-9 text-[13px] mt-1" />
                      </div>
                      <div>
                        <Label className="text-[12px] text-gray-500">Rent Due Date</Label>
                        <Input value={rentDueDate} onChange={e => setRentDueDate(e.target.value)} placeholder="e.g. 1st, 15th" className="h-9 text-[13px] mt-1" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[12px] text-gray-500">Deposit Amount</Label>
                        <Input type="number" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} className="h-9 text-[13px] mt-1" />
                      </div>
                      <div>
                        <Label className="text-[12px] text-gray-500">Deposit Date</Label>
                        <Input type="date" value={depositDate} onChange={e => setDepositDate(e.target.value)} className="h-9 text-[13px] mt-1" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[12px] text-gray-500">Partial First Month</Label>
                        <Input type="number" value={partialFirstMonth} onChange={e => setPartialFirstMonth(e.target.value)} placeholder="Leave blank for full" className="h-9 text-[13px] mt-1" />
                      </div>
                      <div>
                        <Label className="text-[12px] text-gray-500">Partial Last Month</Label>
                        <Input type="number" value={partialLastMonth} onChange={e => setPartialLastMonth(e.target.value)} placeholder="Leave blank for full" className="h-9 text-[13px] mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[12px] text-gray-500">Pets</Label>
                      <Input value={pets} onChange={e => setPets(e.target.value)} className="h-9 text-[13px] mt-1" />
                    </div>
                  </>
                )}

                <div>
                  <Label className="text-[12px] text-gray-500">Notes</Label>
                  <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional notes..." className="text-[13px] mt-1" rows={3} />
                </div>
              </div>
            </div>
          )}
        </div>

        {step >= 4 && (
          <div className="px-5 py-3 border-t bg-gray-50">
            <Button className="w-full h-9 text-[13px]" disabled={!name.trim() || saving} onClick={handleCreate} data-testid="tenant-create-submit">
              {saving ? 'Creating...' : 'Create tenant'}
            </Button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
